import React from "react";

import { Event } from "lib/build/events";

import { api } from "src/common/utils/api";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";

interface FetchEventsOptions {
	id: string;
	concepts: string[];
	timeFrom?: string;
	timeTo?: string;
}

export function useEvents(): readonly [
	boolean,
	Event[],
	(options: FetchEventsOptions) => Promise<void>
] {
	const { org, mod } = useOrgModParams();

	const [isLoading, setLoading] = React.useState(false);
	const [data, setData] = React.useState<Event[]>([]);

	const createError = useCreateGenericError();

	const fetch = React.useCallback(
		async ({
			id,
			concepts,
			timeFrom,
			timeTo
		}: {
			id: string;
			concepts: string[];
			timeFrom?: string;
			timeTo?: string;
		}) => {
			setData([]);
			if (
				concepts.length === 0 ||
				(timeFrom !== undefined && timeTo === undefined) ||
				(timeFrom === undefined && timeTo !== undefined)
			) {
				return;
			}
			setLoading(true);
			try {
				const { data } = await api.get<Event[] | null>(
					`/events/${id}${createEventQuery({
						org,
						mod,
						concepts,
						timeFrom,
						timeTo
					})}`
				);
				setLoading(false);
				setData(data || []);
			} catch {
				setLoading(false);
				createError();
			}
		},
		[createError, org, mod]
	);

	return [isLoading, data, fetch];
}

interface CreateEventQueryOptions {
	org: string;
	mod: string;
	concepts: string[];
	timeFrom?: string;
	timeTo?: string;
}

function createEventQuery({
	org,
	mod,
	concepts,
	timeFrom,
	timeTo
}: CreateEventQueryOptions): string {
	const params = [
		`organization=${org}`,
		`module=${mod}`,
		"includeDeleted=true"
	];
	for (const value of concepts) {
		params.push(`i=${value}`);
	}
	if (timeFrom !== undefined) {
		params.push(`from=${timeFrom}`);
	}
	if (timeTo !== undefined) {
		params.push(`to=${timeTo}`);
	}
	return `?${params.join("&")}`;
}
